import React from 'react';
import styled from 'styled-components';
import DotIcon from '@material-ui/icons/FiberManualRecord';
import { IGeneralInformationProps } from 'components/deal-results/general-information';
import {
  dealFieldUpliftOverBatna,
  dealFieldHeadlineSize,
  dealFieldVolume,
  dealFieldCustomer,
  dealFieldName,
  dealFieldPeriod,
} from 'components/deal-results/general-information/constants';
import colors from 'theme/colors';
import ToolTip from 'components/i-tooltip';
import CostToServeDetails from './cost-to-serve';

const { darkInputBorder, globalIcon } = colors;

const SuperContainer = styled.div``;

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 48px;
`;

const RevenueBox = styled.div`
  border: 1px solid ${darkInputBorder};
  border-radius: 3px;
  min-width: 180px;
  height: 88px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding-left: 8px;
  padding-right: 8px;
`;

const RevenueBoxValue = styled.div`
  font-size: 22px;
  font-weight: 600;
  margin-bottom: 4px;
`;

const RevenueBoxDescription = styled.div`
  color: ${darkInputBorder};
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const RevenueContainer = styled.div`
  display: flex;
  flex: 1;
  justify-content: flex-end;

  ${RevenueBox} {
    margin-left: 12px;
  }
`;

const ToggleSection = styled.div`
  display: flex;

  & > div {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
  }
`;

const Bottom = styled.div`
  padding-left: 16px;
`;

const Clusters = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0px 80px;
`;

const Cluster = styled.div`
  font-size: 14px;
`;

const ClusterTitle = styled.div`
  color: ${darkInputBorder};
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
`;

const ClusterRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 4px;
`;

const ClusterRowValue = styled.div`
  font-size: 18px;
  font-weight: 500;
  margin-right: 8px;
`;

const ClusterRowLabel = styled.div`
  color: ${globalIcon};
  font-weight: 500;
`;

const DealInfoRow = styled.div`
  display: flex;
  align-items: center;
  font-size: 12px;
  margin-bottom: 12px;
`;

const DealInfoLabel = styled.div`
  color: ${globalIcon};
  width: 80px;
  margin-right: 48px;
`;

const DealInfoValue = styled.div`
  display: flex;
  align-items: center;
`;

const DealStatus = styled.div`
  display: flex;
  align-items: center;
`;

const DealStatusIndicator = styled.span`
  margin: 0px 5px;
`;

const DealStatusText = styled.span`
  font-size: 10px;
`;

const DotIconContainer = styled.span`
  position: relative;
  top: 2px;

  svg {
    width: 0.5em;
  }
`;

export default ({
  upliftOverBatna,
  headlineSize,
  volume,
  pricePerUnitLabel,
  contractPriceTotal,
  contractRevenueTotal,
  netbackPriceTotal,
  netbackRevenueTotal,
  marginPriceTotal,
  marginRevenueTotal,
  marginAtRiskPriceTotal,
  marginAtRiskRevenueTotal,
  costToServeYield,
  volumeLabel,
  dealName,
  customerName,
  region,
  productLine,
  period,
  dealStatusIndicatorColor,
  dealStatusText,
}: IGeneralInformationProps) => (
  <SuperContainer>
    <Container>
      <ToggleSection>
        <div>
          <DealInfoRow>
            <DealInfoLabel>{dealFieldName}</DealInfoLabel>
            <DealInfoValue>{dealName}</DealInfoValue>
          </DealInfoRow>
          <DealInfoRow>
            <DealInfoLabel>{dealFieldCustomer}</DealInfoLabel>
            <DealInfoValue>
              {customerName} - {region}, {productLine}
            </DealInfoValue>
          </DealInfoRow>

          <DealInfoRow>
            <DealInfoLabel>{dealFieldPeriod}</DealInfoLabel>
            <DealInfoValue>
              <span>{period}</span>
              <DealStatus>
                <DealStatusIndicator style={{ color: dealStatusIndicatorColor }}>
                  <DotIconContainer>
                    <DotIcon />
                  </DotIconContainer>
                </DealStatusIndicator>
                <DealStatusText>{dealStatusText}</DealStatusText>
              </DealStatus>
            </DealInfoValue>
          </DealInfoRow>
        </div>
      </ToggleSection>

      <RevenueContainer>
        <RevenueBox>
          <RevenueBoxValue>
            {volume} {volumeLabel}
          </RevenueBoxValue>
          <RevenueBoxDescription>{dealFieldVolume}</RevenueBoxDescription>
        </RevenueBox>
        <RevenueBox>
          <RevenueBoxValue>{upliftOverBatna}</RevenueBoxValue>
          <RevenueBoxDescription>{dealFieldUpliftOverBatna}</RevenueBoxDescription>
        </RevenueBox>
        <RevenueBox>
          <RevenueBoxValue>{headlineSize}</RevenueBoxValue>
          <RevenueBoxDescription>
            {dealFieldHeadlineSize}
            <ToolTip
              top="2"
              placement="bottom"
              title="This is based on historical marker prices for the last 12 months."
            />
          </RevenueBoxDescription>
        </RevenueBox>
      </RevenueContainer>
    </Container>
    <Bottom>
      <Clusters>
        <Cluster>
          <ClusterTitle>REVENUE</ClusterTitle>
          <ClusterRow>
            <ClusterRowValue>{contractPriceTotal}</ClusterRowValue>
            <ClusterRowLabel>({pricePerUnitLabel})</ClusterRowLabel>
          </ClusterRow>
          <ClusterRow>
            <ClusterRowValue>{contractRevenueTotal}</ClusterRowValue>
            <ClusterRowLabel>($)</ClusterRowLabel>
          </ClusterRow>
        </Cluster>
        <Cluster>
          <ClusterTitle>NETBACK</ClusterTitle>
          <ClusterRow>
            <ClusterRowValue>{netbackPriceTotal}</ClusterRowValue>
            <ClusterRowLabel>({pricePerUnitLabel})</ClusterRowLabel>
          </ClusterRow>
          <ClusterRow>
            <ClusterRowValue>{netbackRevenueTotal}</ClusterRowValue>
            <ClusterRowLabel>($)</ClusterRowLabel>
          </ClusterRow>
        </Cluster>
        <Cluster>
          <ClusterTitle>MARGIN</ClusterTitle>
          <ClusterRow>
            <ClusterRowValue>{marginPriceTotal}</ClusterRowValue>
            <ClusterRowLabel>({pricePerUnitLabel})</ClusterRowLabel>
          </ClusterRow>
          <ClusterRow>
            <ClusterRowValue>{marginRevenueTotal}</ClusterRowValue>
            <ClusterRowLabel>($)</ClusterRowLabel>
          </ClusterRow>
        </Cluster>
        <Cluster>
          <ClusterTitle>MARGIN AT RISK</ClusterTitle>
          <ClusterRow>
            <ClusterRowValue>{marginAtRiskPriceTotal}</ClusterRowValue>
            <ClusterRowLabel>({pricePerUnitLabel})</ClusterRowLabel>
          </ClusterRow>
          <ClusterRow>
            <ClusterRowValue>{marginAtRiskRevenueTotal}</ClusterRowValue>
            <ClusterRowLabel>($)</ClusterRowLabel>
          </ClusterRow>
        </Cluster>
        <CostToServeDetails costToServeYield={costToServeYield} />
      </Clusters>
    </Bottom>
  </SuperContainer>
);
